import { useQuery } from '@tanstack/react-query';
import { startOfMonth, endOfMonth, format } from 'date-fns';
import { calendarService } from '@/services/calendar.service';
import { CalendarEvent } from '@/types/calendar';

/**
 * React Query hook to fetch calendar events (notes + holidays) for a given calendar and date
 *
 * Fetches events for the entire month containing the provided date.
 *
 * @param calendarId - Calendar ID to fetch events for
 * @param date - Date within the month to fetch events for
 * @returns React Query result with CalendarEvent array
 */
export function useCalendarEvents(
  calendarId: number | null,
  date: Date | null
) {
  return useQuery<CalendarEvent[], Error>({
    queryKey: ['calendar-events', calendarId, date ? format(date, 'yyyy-MM') : null],
    queryFn: async () => {
      if (!calendarId || !date) return [];

      const monthStart = startOfMonth(date);
      const monthEnd = endOfMonth(date);

      return calendarService.getCalendarEvents(
        calendarId,
        monthStart.toISOString(),
        monthEnd.toISOString()
      );
    },
    enabled: !!calendarId && !!date,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
